import React, { useState, useEffect, useRef } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Resize,
  Sort,
  ContextMenu,
  Filter,
  Page,
  ExcelExport,
  PdfExport,
  Inject,
} from "@syncfusion/ej2-react-grids";
import { Header } from "../Components";
import axios from "axios";
import {
  FaSearch,
  FaTrash,
  FaTimes,
  FaFileExcel,
  FaFilePdf,
  FaPlus,
  FaEdit,
  FaFileAlt,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";
// Predefined courses for dropdown
const courses = [
  { value: "P.T.C (D.El.Ed)", label: "P.T.C (D.El.Ed)" },
  // { value: "B.Sc. Nursing", label: "B.Sc. Nursing" },
  // { value: "M.Sc. Nursing", label: "M.Sc. Nursing" },
  // { value: "Diploma in Nursing", label: "Diploma in Nursing" },
];

// Syllabus Grid column configuration
const syllabusGrid = [
  {
    field: "id",
    headerText: "ID",
    width: 80,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">ID</span>,
  },
  {
    field: "title",
    headerText: "Title",
    width: 200,
    textAlign: "Left",
    headerTemplate: () => <span className="font-bold">Title</span>,
  },
  {
    field: "course",
    headerText: "Course",
    width: 150,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Course</span>,
  },
  {
    field: "date_time",
    headerText: "Uploaded On",
    width: 150,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Uploaded On</span>,
  },
  {
    field: "file_path",
    headerText: "File",
    width: 100,
    textAlign: "Center",
    template: (props) => (
      <a
        href={props.file_path}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline flex items-center justify-center"
      >
        <FaFileAlt className="mr-1" /> View PDF
      </a>
    ),
    headerTemplate: () => <span className="font-bold">File</span>,
  },
  {
    field: "Actions",
    headerText: "Actions",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex justify-center gap-3">
        <button
          className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.editSyllabusHandler(props)}
          title="Edit"
        >
          <FaEdit size={18} />
        </button>
        <button
          className="text-red-600 hover:text-red-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.deleteSyllabusHandler(props.id)}
          title="Delete"
        >
          <FaTrash size={18} />
        </button>
      </div>
    ),
  },
];

const Syllabus = () => {
  const [syllabusData, setSyllabusData] = useState([]);
  const [selectedSyllabus, setSelectedSyllabus] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    course: "",
    file: null,
    filePreview: null,
  });

  const { baseUrl } = useBaseUrl();

  const gridRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || `${baseUrl}syllabus.php`;

  // Validate syllabus form
  const validateSyllabusForm = (data, isAdd) => {
    if (!data.title) return "Title is required.";
    if (!data.course) return "Course is required.";
    if (isAdd && !data.file) return "PDF file is required.";
    if (data.file && data.file.type !== "application/pdf")
      return "Please upload a valid PDF file.";
    return null;
  };

  // Fetch syllabus data
  useEffect(() => {
    fetchSyllabus();
  }, []);

  const fetchSyllabus = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const formData = new FormData();
      formData.append("action", "get-syllabus");
      const response = await axios.post(API_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      console.log(response.data);
      if (response.data.status) {
        setSyllabusData(response.data.data || []);
      } else {
        setErrorMessage(response.data.error || "Failed to fetch syllabus.");
      }
    } catch (error) {
      setErrorMessage(
        "Error connecting to the server: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add syllabus
  const handleAddSyllabus = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    const error = validateSyllabusForm(formData, true);
    if (error) {
      setErrorMessage(error);
      setIsLoading(false);
      return;
    }

    try {
      const data = new FormData();
      data.append("action", "add-syllabus");
      data.append("title", formData.title);
      data.append("course", formData.course);
      data.append("syllabus_pdf", formData.file);

      const response = await axios.post(API_URL, data, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (response.data.status) {
        fetchSyllabus();
        // setSyllabusData([...syllabusData, response.data.data]);
        setIsAddModalOpen(false);
        setFormData({ title: "", course: "", file: null, filePreview: null });
        setErrorMessage("");
        alert("Syllabus added successfully.");
      } else {
        setErrorMessage(response.data.error || "Failed to add syllabus.");
      }
    } catch (error) {
      setErrorMessage(
        "Failed to add syllabus: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit syllabus
  const handleEditSyllabus = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");

    const error = validateSyllabusForm(formData, false);
    if (error) {
      setErrorMessage(error);
      setIsLoading(false);
      return;
    }

    try {
      const data = new FormData();
      data.append("action", "update-syllabus");
      data.append("id", selectedSyllabus.id);
      data.append("title", formData.title);
      data.append("course", formData.course);
      if (formData.syllabus_pdf) {
        data.append("syllabus_pdf", formData.file);
      }

      const response = await axios.post(API_URL, data, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      if (response.data.status) {
        fetchSyllabus();
        // setSyllabusData(syllabusData.map(item => item.id === selectedSyllabus.id ? response.data.data : item));
        setIsEditModalOpen(false);
        setFormData({ title: "", course: "", file: null, filePreview: null });
        setSelectedSyllabus(null);
        setErrorMessage("");
        alert("Syllabus updated successfully.");
      } else {
        setErrorMessage(response.data.error || "Failed to update syllabus.");
      }
    } catch (error) {
      setErrorMessage(
        "Failed to update syllabus: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete syllabus
  const deleteSyllabusHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this syllabus?")) {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const data = new FormData();
        data.append("action", "delete-syllabus");
        data.append("id", id);

        const response = await axios.post(API_URL, data, {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        });

        if (response.data.status) {
          setSyllabusData(syllabusData.filter((item) => item.id !== id));
          setErrorMessage("");
          alert("Syllabus deleted successfully.");
        } else {
          setErrorMessage(response.data.error || "Failed to delete syllabus.");
        }
      } catch (error) {
        setErrorMessage(
          "Failed to delete syllabus: " +
            (error.response?.data?.error || error.message)
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle edit syllabus action
  const editSyllabusHandler = (syllabus) => {
    setSelectedSyllabus(syllabus);
    setFormData({
      title: syllabus.title,
      course: syllabus.course,
      file: null,
      filePreview: syllabus.file_path,
    });
    setIsEditModalOpen(true);
  };

  // PDF file preview handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      const previewUrl = URL.createObjectURL(file);
      setFormData({ ...formData, file, filePreview: previewUrl });
    } else {
      setErrorMessage("Please upload a valid PDF file.");
    }
  };

  // Search filter
  const filteredSyllabusData = syllabusData.filter(
    (item) =>
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.course?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Inject handlers into grid data
  const syllabusGridData = filteredSyllabusData.map((item) => ({
    ...item,
    editSyllabusHandler,
    deleteSyllabusHandler,
  }));

  const { setCurrentColor, setCurrentMode, currentMode } = useStateContext();

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
  }, []);

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn bg-gray-100 min-h-screen"
    >
      <Header category="Dashboard" title="Syllabus Management" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0Abhijeet100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Search and Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by title or course..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300 shadow-md"
            onClick={() => {
              setFormData({
                title: "",
                course: "",
                file: null,
                filePreview: null,
              });
              setIsAddModalOpen(true);
            }}
          >
            <FaPlus className="mr-2" /> Add Syllabus
          </button>
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => gridRef.current.excelExport()}
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => gridRef.current.pdfExport()}
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Syllabus Grid */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          ref={gridRef}
          id="syllabusGrid"
          dataSource={syllabusGridData}
          allowPaging={true}
          allowSorting={true}
          allowExcelExport={true}
          allowPdfExport={true}
          allowFiltering={true}
          allowResizing={true}
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: "Menu", showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {syllabusGrid.map((item, index) => (
              <ColumnDirective key={index} {...item} />
            ))}
          </ColumnsDirective>
          <Inject
            services={[
              Resize,
              Sort,
              ContextMenu,
              Filter,
              Page,
              ExcelExport,
              PdfExport,
            ]}
          />
        </GridComponent>
      </div>

      {/* Add Syllabus Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Add Syllabus</h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition"
                onClick={() => setIsAddModalOpen(false)}
              >
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleAddSyllabus} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Course
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.course}
                  onChange={(e) =>
                    setFormData({ ...formData, course: e.target.value })
                  }
                  required
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course.value} value={course.value}>
                      {course.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  PDF File
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  onChange={handleFileChange}
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  disabled={isLoading}
                >
                  Add Syllabus
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Syllabus Modal */}
      {isEditModalOpen && selectedSyllabus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Edit Syllabus
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition"
                onClick={() => setIsEditModalOpen(false)}
              >
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSyllabus} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Title
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Course
                </label>
                <select
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.course}
                  onChange={(e) =>
                    setFormData({ ...formData, course: e.target.value })
                  }
                  required
                >
                  <option value="">Select Course</option>
                  {courses.map((course) => (
                    <option key={course.value} value={course.value}>
                      {course.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  PDF File (optional)
                </label>
                <input
                  type="file"
                  accept="application/pdf"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  onChange={handleFileChange}
                />
                {formData.filePreview && (
                  <a
                    href={formData.filePreview}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center mt-2"
                  >
                    <FaFileAlt className="mr-1" /> View Current PDF
                  </a>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  disabled={isLoading}
                >
                  Update Syllabus
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Syllabus;
